<script lang="ts">
  import * as Card from "$lib/components/ui/card/index.js";
  import { isEditActive } from "$src/application/main/mainTemplateStore";
  import DeliveryTypeEdit from "../../../deliveries/components/delivery-type-edit.svelte";
  import { Label } from "$lib/components/ui/label";
  import { Textarea } from "$lib/components/ui/textarea";
  import {
    projectDraft,
    ProjectFormSection,
    projectGeoCodesChanged,
    projectInstallationAndDeliveryChanged,
    projectOriginal,
    registerErrors,
    roadStore,
  } from "$src/application/projects/stores/projectStore";
  import { get } from "svelte/store";
  import { getTranslation } from "$src/presentation/core/TranslatedTextUtils";
  import { languageCode } from "$src/presentation/core/languageCode";
  import { t } from "svelte-i18n";
  import { Switch } from "$lib/components/ui/switch";
  import AddressValue from "$src/presentation/core/address-value.svelte";
  import DeliveryMap from "./delivery-map.svelte";
  import InstallationTypeEdit from "$src/presentation/projects/installations/components/installation-type-edit.svelte";
  import { type Geocode } from "$src/domain/map/GeoCode";
  import { type RoadFromPoints } from "$src/application/map/commands/ComputeRoadFromPoints";
  import { type InstallationTypeValue } from "$src/domain/projects/installations/InstallationType";
  import { type DeliveryType } from "$src/domain/projects/deliveries/DeliveryType";
  import { Checkbox } from "$lib/components/ui/checkbox";
  import { formatDecimal } from "$src/presentation/core/currencyUtils";

  export let patchHandler: () => void;
  export let groupStoreId: number;

  $: requireAddress = $isEditActive
    ? $projectDraft.delivery.deliveryType?.requireAddress
    : $projectOriginal.delivery.deliveryType?.requireAddress;

  function updateRoad() {
    if ($projectDraft) {
      if ($projectDraft.delivery.isCustomerAddress) {
        const points: RoadFromPoints = {
          from: $projectDraft.delivery.geoLocationFrom,
          to: $projectDraft.delivery.geoLocationTo,
        };

        projectGeoCodesChanged.set(points);
        console.log("to compute", points);
      } else {
        const points: RoadFromPoints = {
          from: $projectDraft.delivery.geoLocationFrom,
          to: $projectDraft.customer.billingAddressLocation,
        };

        projectGeoCodesChanged.set(points);
        console.log("to compute", points);
      }
    }
  }

  interface FormErrors {
    [key: string]: string[] | null;
  }

  function onChanged(e: CustomEvent<InstallationTypeValue>) {
    const errors: FormErrors = {};
    registerErrors(ProjectFormSection.Amounts, errors);
    projectInstallationAndDeliveryChanged.update((value) => value + 1);
  }

  function onDeliveryTypeChanged(e: CustomEvent<DeliveryType>) {
    projectInstallationAndDeliveryChanged.update((value) => value + 1);
  }

  function addressGeocoded(e: CustomEvent<Geocode>) {
    if ($isEditActive) {
      $projectDraft.delivery.geoLocationTo = e.detail.location;

      const points: RoadFromPoints = {
        from: $projectDraft.delivery.geoLocationFrom,
        to: $projectDraft.delivery.geoLocationTo,
      };

      projectGeoCodesChanged.set(points);
    }
  }
</script>

<Card.Root class="flex-auto">
  <Card.Header class="pb-4">
    <Card.Title>
      {$t("page.projects.detail.delivery.title")}
    </Card.Title>
  </Card.Header>
  <Card.Content class="flex flex-col md:flex-row gap-4 flex-wrap md:flex-nowrap ">
    <div class="flex-1 flex flex-col gap-1">
      <!-- Type de pose  -->
      <div class="flex flex-col">
        <Label class="text-xs font-normal text-muted-foreground ">
          {$t("page.projects.detail.installation.type")}
        </Label>
        {#if $isEditActive}
          <InstallationTypeEdit
            {groupStoreId}
            bind:value={$projectDraft.installation.type}
            on:change={patchHandler}
            on:change={onChanged}
          />
        {:else}
          <Label class="w-full p-2 font-normal">
            {$t(
              `page.projects.detail.installation.types.${$projectOriginal.installation?.type}`
            )}
          </Label>
        {/if}
      </div>
      <!-- Mode de livraison -->
      <div class="flex flex-col">
        <Label class="text-xs font-normal text-muted-foreground ">
          {$t("page.projects.detail.delivery.type")}
        </Label>
        {#if $isEditActive}
          <DeliveryTypeEdit
            {groupStoreId}
            bind:value={$projectDraft.delivery.deliveryType}
            on:change={patchHandler}
            on:change={onDeliveryTypeChanged}
          />
        {:else if $projectOriginal.delivery.deliveryType}
          <Label class="w-full p-2 font-normal">
            {getTranslation(
              $projectOriginal.delivery.deliveryType?.translations,
              $languageCode
            )}
          </Label>
        {/if}
      </div>
      <!-- Adresse de livraison -->
      {#if requireAddress}
        <div>
          <Label class="text-xs font-normal text-muted-foreground ">
            {$t("page.projects.detail.delivery.address")}
          </Label>
          {#if $isEditActive}
            <div class="flex items-center space-x-2 pb-4">
              <Checkbox
                id="delivery-customer-address"
                on:click={updateRoad}
                bind:checked={$projectDraft.delivery.isCustomerAddress}
              />
              <Label for="delivery-customer-address">
                {$t("page.projects.detail.delivery.customerAddress")}
              </Label>
            </div>
            {#if !$projectDraft.delivery.isCustomerAddress && $projectDraft.delivery.addressTo}
              <AddressValue
                bind:value={$projectDraft.delivery.addressTo}
                {patchHandler}
                editMode={true}
                geoCodeActivated={true}
                displayGeoCode={true}
                on:geoCoded={addressGeocoded}
              />
            {:else}
              <Label class="font-normal">
                <AddressValue
                  bind:value={$projectDraft.customer.billingAddress}
                />
              </Label>
            {/if}
          {:else}
            <div class="flex flex-col">
              {#if $projectOriginal.delivery.isCustomerAddress}
                <Label class="w-full p-2 font-normal">
                  {$t("page.projects.detail.delivery.customerAddress")}
                </Label>
              {/if}
              {#if $projectOriginal.delivery.addressTo}
                <Label class="w-full p-2 font-normal">
                  <AddressValue value={$projectOriginal.delivery.addressTo} />
                </Label>
              {/if}
            </div>
          {/if}
        </div>
      {/if}
      <!-- Commentaire -->
      <div>
        {#if $isEditActive}
          <Label class="text-xs font-normal text-muted-foreground ">
            {$t("page.projects.detail.delivery.comment")}
          </Label>
          <Textarea
            maxlength={150}
            class="resize-none h-20"
            on:input={patchHandler}
            bind:value={$projectDraft.delivery.comment}
          />
        {:else if $projectOriginal.delivery.comment}
          <div class="flex flex-col">
            <Label class="text-xs font-normal text-muted-foreground ">
              {$t("page.projects.detail.delivery.comment")}
            </Label>
            <Label class="w-full p-2 font-normal whitespace-pre-wrap">
              {$projectOriginal.delivery.comment}
            </Label>
          </div>
        {/if}
      </div>
    </div>
    {#if $isEditActive}
      {#if $projectDraft.delivery.deliveryType?.requireAddress}
        <div class="flex-1 flex flex-col w-full gap-2">
          <div
            class="flex flex-row text-xs justify-between items-center italic"
          >
            {#if $roadStore}
              <div>{formatDecimal($roadStore.timeMinutes ?? 0)} minutes</div>
              <div>{formatDecimal($roadStore.distanceKilometers ?? 0)} km</div>
            {/if}
          </div>
          <div class="flex-1 w-full border-2 rounded-lg shadow-xl min-w-44">
            <DeliveryMap />
          </div>
        </div>
      {/if}
    {:else if $projectOriginal.delivery.deliveryType?.requireAddress}
      <div class="flex-1 flex flex-col w-full gap-2">
        <div class="flex flex-row text-xs justify-between items-center italic">
          {#if $roadStore}
            <div>{formatDecimal($roadStore.timeMinutes ?? 0)} minutes</div>
            <div>{formatDecimal($roadStore.distanceKilometers ?? 0)} km</div>
          {/if}
        </div>
        <div class="flex-1 w-full border-2 rounded-lg shadow-xl min-w-44">
          <DeliveryMap />
        </div>
      </div>
    {/if}
  </Card.Content>
</Card.Root>
